import React, { useEffect, useMemo, useState } from 'react';
import { 
  Box, 
  Button, 
  Typography, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Stack, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  TextField, 
  Alert,
  CircularProgress,
  Skeleton,
  IconButton,
  Tooltip,
  useMediaQuery,
  useTheme,
  Fade,
  InputAdornment,
  Pagination,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  TableSortLabel,
  TablePagination,
  Tabs,
  Tab,
  Switch,
  FormControlLabel,
  Breadcrumbs,
  Link,
  Popover,
  Autocomplete,
  TextField as MuiTextField
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Business as BusinessIcon,
  Work as WorkIcon,
  Refresh as RefreshIcon,
  VpnKey as VpnKeyIcon,
  ToggleOn as ToggleOnIcon,
  ToggleOff as ToggleOffIcon,
  GetApp as ExportIcon,
  Print as PrintIcon,
  PictureAsPdf as PdfIcon,
  Clear as ClearIcon,
  FilterList as FilterListIcon,
  Store as StoreIcon,
  Group as GroupIcon,
} from '@mui/icons-material';
import { listEmployeesApi, createEmployeeApi, updateEmployeeApi, deleteEmployeeApi, transferEmployeeApi, approveEmployeeApi, rejectEmployeeApi } from '../api/employees';
import { getGroupsFromTimekeep, getStoresFromTimekeep } from '../api/timekeep';
import { useAuth } from '../context/AuthContext';
import { useSnackbar } from 'notistack';
import { motion, AnimatePresence } from 'framer-motion';
import { Icon } from '@iconify/react';

const DEDUCTION_ACCESS_OWNER_EMAIL = 'a.domacena@sparkles.com.ph';
const DEDUCTION_ACCESS_OWNER_ROLE = 'admin' || 'super-admin';
const DEDUCTION_ACCESS_OPTIONS = ['fgi', 'procurement', 'insurance'];
const PAYROLL_ACCESS_TABS = ['masterfile', 'payroll', 'employees', 'reports', 'settings'];
const DEFAULT_PAYROLL_ACCESS_CONTROL = {
  masterfile: true,
  payroll: true,
  employees: true,
  reports: true,
  settings: true,
};
const normalizePayrollAccessControl = (value) => {
  const input = value && typeof value === 'object' ? value : {};
  return {
    masterfile: input.masterfile !== undefined ? Boolean(input.masterfile) : true,
    payroll: input.payroll !== undefined ? Boolean(input.payroll) : true,
    employees: input.employees !== undefined ? Boolean(input.employees) : true,
    reports: input.reports !== undefined ? Boolean(input.reports) : true,
    settings: input.settings !== undefined ? Boolean(input.settings) : true,
  };
};

const emptyForm = { firstName: '', lastName: '', email: '', password: '', position: '', department: '', company: '', role: '', isActive: true, approvalStatus: 'pending', storeAccess: [], groupAccess: [], deductionAccess: [], payrollAccessControl: { ...DEFAULT_PAYROLL_ACCESS_CONTROL } };
const emptyPasswordForm = { newPassword: '', confirmPassword: '' };

const Employee = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [refreshing, setRefreshing] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [tabValue, setTabValue] = useState(0);
  const [mainTabValue, setMainTabValue] = useState(0); // For All Employees vs Rejected Employees tabs
  const [passwordForm, setPasswordForm] = useState(emptyPasswordForm);
  const [passwordValidationErrors, setPasswordValidationErrors] = useState({});
  const [passwordTouched, setPasswordTouched] = useState({});
  
  // Enhanced table state
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('firstName');
  const [dense, setDense] = useState(false);

  // Filtering state
  const [filters, setFilters] = useState({
    status: 'all', // 'all', 'active', 'inactive'
    company: 'all',
    position: 'all',
    department: 'all'
  });
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [filterColumn, setFilterColumn] = useState(null);

  // Groups data for autocomplete
  const [availableGroups, setAvailableGroups] = useState([]);
  const [groupsLoading, setGroupsLoading] = useState(false);

  // Stores data for autocomplete
  const [availableStores, setAvailableStores] = useState([]);
  const [storesLoading, setStoresLoading] = useState(false);

  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super-admin';
  //const canManageDeductionAccess = user?.email?.toLowerCase() === DEDUCTION_ACCESS_OWNER_EMAIL;
  const canManageDeductionAccess = user?.role === DEDUCTION_ACCESS_OWNER_ROLE;

  const { enqueueSnackbar } = useSnackbar();

  // Filter helper functions
  const handleFilterClick = (event, column) => {
    setFilterAnchorEl(event.currentTarget);
    setFilterColumn(column);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
    setFilterColumn(null);
  };

  const handleFilterChange = (column, value) => {
    setFilters(prev => ({
      ...prev,
      [column]: value
    }));
    setCurrentPage(1);
  };

  const clearAllFilters = () => {
    setFilters({
      status: 'all',
      company: 'all',
      position: 'all',
      department: 'all'
    });
    setCurrentPage(1);
  };



  // Get unique values for filters
  const uniqueCompanies = useMemo(() => {
    return [...new Set(rows.map(row => row.company).filter(Boolean))].sort();
  }, [rows]);

  const uniquePositions = useMemo(() => {
    return [...new Set(rows.map(row => row.position).filter(Boolean))].sort();
  }, [rows]);

  const uniqueDepartments = useMemo(() => {
    return [...new Set(rows.map(row => row.department).filter(Boolean))].sort();
  }, [rows]);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Validation functions
  const validateField = (field, value) => {
    let error = null;
    switch (field) {
      case 'firstName':
        if (!value?.trim()) error = 'First name is required';
        else if (value.trim().length < 2) error = 'First name must be at least 2 characters';
        break;
      case 'lastName':
        if (!value?.trim()) error = 'Last name is required';
        else if (value.trim().length < 2) error = 'Last name must be at least 2 characters';
        break;
      case 'email':
        if (!value?.trim()) error = 'Email is required';
        else if (!/^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/.test(value)) error = 'Please enter a valid email address';
        break;
      case 'password':
        if (!editingId && (!value || value.length < 6)) error = 'Password must be at least 6 characters';
        break;
      case 'position':
        if (!value?.trim()) error = 'Position is required';
        break;
      case 'department':
        if (!value?.trim()) error = 'Department is required';
        break;
      case 'company':
        if (!value?.trim()) error = 'Company is required';
        break;
    }

    setValidationErrors(prev => ({ ...prev, [field]: error }));
    return error === null;
  };

  // Password validation functions
  const validatePasswordField = (field, value) => {
    let error = null;
    switch (field) {
      case 'newPassword':
        if (!value || value.length < 6) error = 'Password must be at least 6 characters';
        break;
      case 'confirmPassword':
        if (value !== passwordForm.newPassword) error = 'Passwords do not match';
        break;
    }

    setPasswordValidationErrors(prev => ({ ...prev, [field]: error }));
    return error === null;
  };

  // Sorting utility functions
  const descendingComparator = (a, b, orderBy) => {
    if (b[orderBy] < a[orderBy]) {
      return -1;
    }
    if (b[orderBy] > a[orderBy]) {
      return 1;
    }
    return 0;
  };

  const getComparator = (order, orderBy) => {
    return order === 'desc'
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  };

  const stableSort = (array, comparator) => {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) {
        return order;
      }
      return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
  };

  // Enhanced table handlers
  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listEmployeesApi();
      if (data?.status === 'success') {
        setRows(data.data);
        enqueueSnackbar('Employees loaded successfully', { variant: 'success' });
      } else {
        const errorMsg = data?.message || 'Failed to load employees';
        setError(errorMsg);
        enqueueSnackbar(errorMsg, { variant: 'error' });
      }
    } catch (e) {
      const errorMsg = e?.response?.data?.message || 'Failed to load employees';
      setError(errorMsg);
      enqueueSnackbar(errorMsg, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
  };

  useEffect(() => { fetchData(); }, []);

  // Fetch available groups for autocomplete
  useEffect(() => {
    const fetchGroups = async () => {
      setGroupsLoading(true);
      try {
        const response = await getGroupsFromTimekeep();
        if (response.success) {
          const formattedGroups = response.data
            .map((group) => {
              if (typeof group === 'string') return group.trim();
              return group?.companyName || group?.group || group?.name || '';
            })
            .filter(Boolean);
          setAvailableGroups(formattedGroups);
        } else {
          enqueueSnackbar('Failed to load groups', { variant: 'error' });
        }
      } catch (error) {
        console.error('Error fetching groups:', error);
        enqueueSnackbar('Error loading groups', { variant: 'error' });
      } finally {
        setGroupsLoading(false);
      }
    };
    if (isSuperAdmin) {
      fetchGroups();
    }
  }, [isSuperAdmin]); 

  const fetchStores = async () => {
    setStoresLoading(true);
    try {

      const response = await getStoresFromTimekeep(user?.groupAccess);
      console.log(response.data)
      if (response.success) {
        const formattedStores = response.data
        setAvailableStores(formattedStores);
      } else {
        enqueueSnackbar('Failed to load stores', { variant: 'error' });
      }
    } catch (error) {
      console.error('Error fetching stores:', error);
      enqueueSnackbar('Error loading stores', { variant: 'error' });
    } finally {
      setStoresLoading(false);
    }
  };

  // Fetch available stores for autocomplete
  useEffect(() => {
    if (!isSuperAdmin && user?.groupAccess.length === 0) {
      return
    } 
    fetchStores();
  }, [isSuperAdmin]);

  // No extra API: derive assignable stores from user's access and existing employees

  // Compute assignable options based on current user
  const assignableGroups = useMemo(() => {
    if (isSuperAdmin) return availableGroups;
    // For non-super-admin users, use only their own group access
    const userGroups = Array.isArray(user?.groupAccess) ? user.groupAccess : [];
    return userGroups;
  }, [isSuperAdmin, user, availableGroups]);

  const assignableStores = useMemo(() => {
    if (isSuperAdmin) return availableStores;
    const userStores = new Set(Array.isArray(user?.storeAccess) ? user.storeAccess : []);
    const availableCombinedStores = [];
    availableCombinedStores.push(...availableStores);
    userStores.forEach(s => {
      if (!availableCombinedStores.includes(s)) {
        availableCombinedStores.push(s);
      }
    });
    return availableCombinedStores
  }, [isSuperAdmin, availableStores, rows, user]);

  // Compute role options based on current user role
  const roleOptions = useMemo(() => {
    if (user?.role === 'super-admin') return ['admin', 'group', 'store'];
    if (user?.role === 'admin') return ['group', 'store'];
    if (user?.role === 'group') return ['store'];
    return [];
  }, [user]); 

  const handleOpen = (row) => {
    if (row) {
      setEditingId(row._id);
      setForm({
        firstName: row.firstName,
        lastName: row.lastName,
        email: row.email,
        password: '',
        position: row.position || '',
        department: row.department || '',
        company: row.company || '',
        role: row.role || (roleOptions[0] || ''),
        isActive: row.isActive !== undefined ? row.isActive : true,
        approvalStatus: row.approvalStatus || 'pending',
        storeAccess: Array.isArray(row.storeAccess) ? row.storeAccess : [],
        groupAccess: Array.isArray(row.groupAccess) ? row.groupAccess : [],
        deductionAccess: Array.isArray(row.deductionAccess) ? row.deductionAccess : [],
        payrollAccessControl: normalizePayrollAccessControl(row.payrollAccessControl)
      });
      // For non super-admin users, the access controls will be disabled if they have no access to assign
      // But we still show the employee's actual current access values
      setPasswordForm(emptyPasswordForm);
      setTabValue(0);
    } else {
      setEditingId(null);
      const defaultRole = roleOptions[0] || '';
      const base = { ...emptyForm, role: defaultRole };
      // For non super-admin, prefill with user's access and lock
      const prefilled = !isSuperAdmin
        ? {
            ...base,
            groupAccess: [],
            storeAccess: []
          }
        : base;
      setForm(prefilled);
      setPasswordForm(emptyPasswordForm);
      setTabValue(0);
    }
    setValidationErrors({});
    setTouched({});
    setPasswordValidationErrors({});
    setPasswordTouched({});
    setOpen(true);
  };

  const validateForm = () => {
    const fields = ['firstName', 'lastName', 'email', 'position', 'department', 'company'];
    if (!editingId) fields.push('password');

    let isValid = true;
    const newTouched = {};

    fields.forEach(field => {
      newTouched[field] = true;
      if (!validateField(field, form[field])) {
        isValid = false;
      }
    });

    // Require at least one of group/store access
    const hasGroups = Array.isArray(form.groupAccess) && form.groupAccess.length > 0;
    const hasStores = Array.isArray(form.storeAccess) && form.storeAccess.length > 0;
    if (!hasGroups && !hasStores) {
      isValid = false;
      newTouched.groupAccess = true;
      newTouched.storeAccess = true;
      setValidationErrors(prev => ({ ...prev, groupAccess: 'Select at least one group or one store', storeAccess: 'Select at least one group or one store' }));
    } else {
      setValidationErrors(prev => ({ ...prev, groupAccess: null, storeAccess: null }));
    }

    setTouched(newTouched);
    return isValid;
  };

  const validatePasswordForm = () => {
    const fields = ['newPassword', 'confirmPassword'];

    let isValid = true;
    const newTouched = {};

    fields.forEach(field => {
      newTouched[field] = true;
      if (!validatePasswordField(field, passwordForm[field])) {
        isValid = false;
      }
    });

    setPasswordTouched(newTouched);
    return isValid;
  };

  const handleSave = async () => {
    if (tabValue === 0) {
      // Employee information tab
      if (!validateForm()) {
        enqueueSnackbar('Please fix the validation errors', { variant: 'error' });
        return;
      }
    } else if (tabValue === 1) {
      // Password change tab
      if (!validatePasswordForm()) {
        enqueueSnackbar('Please fix the password validation errors', { variant: 'error' });
        return;
      }
    }

    setLoading(true);
    try {
      if (editingId) {
        if (tabValue === 0) {
          // Update employee information
          const payload = { ...form };
          delete payload.password; // editing does not change password in info tab
          const data = await updateEmployeeApi(editingId, payload);
          if (data?.status === 'success') {
            await fetchData();
            enqueueSnackbar('Employee updated successfully', { variant: 'success' });
            setOpen(false);
          } else {
            const errorMsg = data?.message || 'Update failed';
            setError(errorMsg);
            enqueueSnackbar(errorMsg, { variant: 'error' });
          }
        } else if (tabValue === 1) {
          // Update password
          const payload = { password: passwordForm.newPassword };
          const data = await updateEmployeeApi(editingId, payload);
          if (data?.status === 'success') {
            await fetchData();
            enqueueSnackbar('Password updated successfully', { variant: 'success' });
            setPasswordForm(emptyPasswordForm);
            setPasswordValidationErrors({});
            setPasswordTouched({});
            setTabValue(0); // Switch back to info tab
          } else {
            const errorMsg = data?.message || 'Password update failed';
            setError(errorMsg);
            enqueueSnackbar(errorMsg, { variant: 'error' });
          }
        }
      } else {
        // Create new employee
        // For non-super-admin users, use their access as defaults for new employees
        const payload = { ...form };
        const data = await createEmployeeApi(payload);
        if (data?.status === 'success') {
          await fetchData();
          enqueueSnackbar('Employee created successfully', { variant: 'success' });
          setOpen(false);
        } else {
          const errorMsg = data?.message || 'Create failed';
          setError(errorMsg);
          enqueueSnackbar(errorMsg, { variant: 'error' });
        }
      }
    } catch (e) {
      const errorMsg = e?.response?.data?.message || 'Request failed';
      setError(errorMsg);
      enqueueSnackbar(errorMsg, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Are you sure you want to delete ${row.firstName} ${row.lastName}?`)) {
      return;
    }

    setLoading(true);
    try {
      const data = await deleteEmployeeApi(row._id);
      if (data?.status === 'success') {
        await fetchData();
        enqueueSnackbar('Employee deleted successfully', { variant: 'success' });
      } else {
        const errorMsg = data?.message || 'Delete failed';
        setError(errorMsg);
        enqueueSnackbar(errorMsg, { variant: 'error' });
      }
    } catch (e) {
      const errorMsg = e?.response?.data?.message || 'Delete failed';
      setError(errorMsg);
      enqueueSnackbar(errorMsg, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const [transferOpen, setTransferOpen] = useState(false);
  const [transferTarget, setTransferTarget] = useState(null); // employee row
  const [transferMotherId, setTransferMotherId] = useState('');
  const potentialMothers = useMemo(() => {
    // build list of eligible mothers: exclude super-admin, and only users in rows with role != 'store'
    // authority rules: group users can only transfer to their own motherId
    const candidates = rows.filter(r => r.role && r.role !== 'store' && r.role !== 'super-admin');
    if (user?.role === 'group') {
      return candidates.filter(r => String(r._id) === String(user?.motherId));
    }
    return candidates;
  }, [rows, user]);

  const openTransfer = (row) => {
    setTransferTarget(row);
    setTransferMotherId('');
    setTransferOpen(true);
  };

  const doTransfer = async () => {
    if (!transferTarget?._id || !transferMotherId) return;
    setLoading(true);
    try {
      const resp = await transferEmployeeApi(transferTarget._id, transferMotherId);
      if (resp?.status === 'success') {
        enqueueSnackbar('Employee transferred successfully', { variant: 'success' });
        setTransferOpen(false);
        await fetchData();
      } else {
        enqueueSnackbar(resp?.message || 'Transfer failed', { variant: 'error' });
      }
    } catch (e) {
      const errorMsg = e?.response?.data?.message || 'Transfer failed';
      enqueueSnackbar(errorMsg, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Approval flow state
  const [approveOpen, setApproveOpen] = useState(false);
  const [approveTarget, setApproveTarget] = useState(null);
  const [approveRemarks, setApproveRemarks] = useState('');
  const [approveGroups, setApproveGroups] = useState([]);
  const [approveStores, setApproveStores] = useState([]);

  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectRemarks, setRejectRemarks] = useState('');

  const openApprove = (row) => {
    setApproveTarget(row);
    setApproveRemarks('');
    setApproveGroups([]);
    setApproveStores([]);
    setApproveOpen(true);
  };
  const openReject = (row) => {
    setRejectTarget(row);
    setRejectRemarks('');
    setRejectOpen(true);
  };

  const confirmApprove = async () => {
    if (!approveTarget?._id) return;
    if (approveGroups.length === 0 && approveStores.length === 0) {
      enqueueSnackbar('Assign at least one group or one store', { variant: 'error' });
      return;
    }
    setLoading(true);
    try {
      const payload = {
        groupAccess: approveGroups,
        storeAccess: approveStores,
        remarks: approveRemarks
      };
      const resp = await approveEmployeeApi(approveTarget._id, payload);
      if (resp?.status === 'success') {
        enqueueSnackbar('Account approved', { variant: 'success' });
        setApproveOpen(false);
        await fetchData();
      } else {
        enqueueSnackbar(resp?.message || 'Approve failed', { variant: 'error' });
      }
    } catch (e) {
      const errorMsg = e?.response?.data?.message || 'Approve failed';
      enqueueSnackbar(errorMsg, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const confirmReject = async () => {
    if (!rejectTarget?._id) return;
    setLoading(true);
    try {
      const payload = {
        remarks: rejectRemarks
      };
      const resp = await rejectEmployeeApi(rejectTarget._id, payload);
      if (resp?.status === 'success') {
        enqueueSnackbar('Account rejected', { variant: 'warning' });
        setRejectOpen(false);
        await fetchData();
      } else {
        enqueueSnackbar(resp?.message || 'Reject failed', { variant: 'error' });
      }
    } catch (e) {
      const errorMsg = e?.response?.data?.message || 'Reject failed';
      enqueueSnackbar(errorMsg, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Enhanced filter, sort and pagination logic
  const filteredRows = useMemo(() => {
    return rows.filter(row => {
      // Tab filter - filter by approval status based on selected tab
      const matchesTab = mainTabValue === 0
        ? row.approvalStatus !== 'rejected' // All Employees tab (exclude rejected)
        : row.approvalStatus === 'rejected'; // Rejected tab (only rejected)

      // Text search
      const matchesSearch = !searchTerm ||
        row.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        row.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        row.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        row.position?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        row.department?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        row.company?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (row.storeAccess && row.storeAccess.some(store => store.toLowerCase().includes(searchTerm.toLowerCase()))) ||
        (row.groupAccess && row.groupAccess.some(group => group.toLowerCase().includes(searchTerm.toLowerCase())));

      // Status filter
      const matchesStatus = filters.status === 'all' ||
        (filters.status === 'active' && row.isActive) ||
        (filters.status === 'inactive' && !row.isActive);

      // Company filter
      const matchesCompany = filters.company === 'all' ||
        row.company === filters.company;

      // Position filter
      const matchesPosition = filters.position === 'all' ||
        row.position === filters.position;

      // Department filter
      const matchesDepartment = filters.department === 'all' ||
        row.department === filters.department;

      return matchesTab && matchesSearch && matchesStatus && matchesCompany && matchesPosition && matchesDepartment;
    });
  }, [rows, searchTerm, filters, mainTabValue]);

  // Apply sorting
  const sortedRows = useMemo(() => {
    return stableSort(filteredRows, getComparator(order, orderBy));
  }, [filteredRows, order, orderBy]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / rowsPerPage));
  const startIndex = (currentPage - 1) * rowsPerPage;
  const paginatedSortedRows = useMemo(() => {
    return sortedRows.slice(startIndex, startIndex + rowsPerPage);
  }, [sortedRows, startIndex, rowsPerPage]);

  useEffect(() => {
    const nextTotalPages = Math.max(1, Math.ceil(filteredRows.length / rowsPerPage));
    if (currentPage > nextTotalPages) {
      setCurrentPage(nextTotalPages);
    }
  }, [filteredRows.length, rowsPerPage, currentPage]);

  if (loading && rows.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton variant="text" width="200px" height={40} sx={{ mb: 3 }} />
        <Paper sx={{ p: 2, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Skeleton variant="rectangular" width="200px" height={36} />
            <Skeleton variant="rectangular" width="120px" height={36} />
          </Box>
          {[...Array(5)].map((_, index) => (
            <Skeleton key={index} variant="rectangular" width="100%" height={60} sx={{ mb: 1, borderRadius: 1 }} />
          ))}
        </Paper>
      </Box>
    );
  }

  return (
    <Fade in timeout={600}>
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', sm: 'center' },
            mb: 3,
            gap: { xs: 2, sm: 0 }
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography
                variant={isMobile ? "h5" : "h4"}
                component="h1"
                sx={{ fontWeight: 'bold', color: '#2c3e50', display: 'flex', alignItems: 'center', gap: 1 }}
              >
                <PersonIcon sx={{ color: '#12C1DC' }} />
                Employees
              </Typography>
              <Tooltip title="Refresh data">
                <IconButton
                  onClick={handleRefresh}
                  disabled={refreshing}
                  sx={{
                    color: '#12C1DC',
                    '&:hover': { bgcolor: 'rgba(18, 193, 220, 0.1)' }
                  }}
                >
                  {refreshing ? (
                    <CircularProgress size={20} sx={{ color: '#12C1DC' }} />
                  ) : (
                    <RefreshIcon />
                  )}
                </IconButton>
              </Tooltip>
            </Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpen(null)}
              disabled={loading}
              sx={{
                bgcolor: '#12C1DC',
                '&:hover': { bgcolor: '#0FABDB' },
                borderRadius: 2,
                textTransform: 'none',
                fontSize: { xs: '0.875rem', md: '1rem' }
              }}
            >
              Add Employee
            </Button>
          </Box>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
        >
          <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 3 }}>
            <Link underline="hover" color="inherit" href="/">
              Dashboard
            </Link>
            <Typography color="text.primary">Employees</Typography>
          </Breadcrumbs>
        </motion.div>

        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
          </motion.div>
        )}


        {/* Employee Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.2 }}
        >
          <Box sx={{ mb: 3 }}>
            <Tabs
              value={mainTabValue}
              onChange={(event, newValue) => setMainTabValue(newValue)}
              sx={{
                borderBottom: 1,
                borderColor: 'divider',
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 500,
                  minHeight: 48,
                  fontSize: '0.95rem'
                }
              }}
            >
              <Tab
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonIcon sx={{ fontSize: '1.1rem' }} />
                    All Employees
                    <Chip
                      label={rows.filter(row => row.approvalStatus !== 'rejected').length}
                      size="small"
                      sx={{
                        ml: 1,
                        height: '20px',
                        fontSize: '0.7rem',
                        bgcolor: '#e3f2fd',
                        color: '#1976d2'
                      }}
                    />
                  </Box>
                }
              />
              <Tab
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Icon icon="mdi:account-cancel" width={18} height={18} />
                    Rejected Employees
                    <Chip
                      label={rows.filter(row => row.approvalStatus === 'rejected').length}
                      size="small"
                      sx={{
                        ml: 1,
                        height: '20px',
                        fontSize: '0.7rem',
                        bgcolor: '#ffebee',
                        color: '#c62828'
                      }}
                    />
                  </Box>
                }
              />
            </Tabs>
          </Box>
        </motion.div>

        {/* Controls and Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <Paper sx={{ borderRadius: 2, overflow: 'hidden', border: '1px solid #e5e7eb' }}>
            {/* Table Controls */}
            <Box sx={{ p: 3, bgcolor: '#f8f9fa', borderBottom: '1px solid #e5e7eb' }}>
              <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'stretch', md: 'center' },
                gap: 2
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                  <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 500, whiteSpace: 'nowrap' }}>
                    Show
                  </Typography>
                  <Select
                    value={rowsPerPage}
                    onChange={(e) => {
                      setRowsPerPage(e.target.value);
                      setCurrentPage(1);
                    }}
                    size="small"
                    sx={{ minWidth: 80 }}
                  >
                    <MenuItem value={5}>5</MenuItem>
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={25}>25</MenuItem>
                    <MenuItem value={50}>50</MenuItem>
                  </Select>
                  <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 500 }}>
                    entries
                  </Typography>
                </Box>

                <TextField
                  size="small"
                  placeholder="Search employees..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: '#9ca3af' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    minWidth: { xs: 'auto', md: '300px' },
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': { borderColor: '#12C1DC' },
                      '&.Mui-focused fieldset': { borderColor: '#12C1DC' }
                    }
                  }}
                />


                <Box sx={{
                  display: 'flex',
                  gap: 1,
                  justifyContent: { xs: 'center', md: 'flex-end' }
                }}>
                  <Tooltip title="Export CSV">
                    <IconButton
                      size="small"
                      onClick={() => handleExport('CSV')}
                      sx={{
                        border: '1px solid #e5e7eb',
                        borderRadius: 2,
                        '&:hover': { bgcolor: '#f9fafb', borderColor: '#12C1DC' }
                      }}
                    >
                      <ExportIcon fontSize="small" sx={{ color: '#6b7280' }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Export Excel">
                    <IconButton
                      size="small"
                      onClick={() => handleExport('Excel')}
                      sx={{
                        border: '1px solid #e5e7eb',
                        borderRadius: 2,
                        '&:hover': { bgcolor: '#f9fafb', borderColor: '#12C1DC' }
                      }}
                    >
                      <Icon icon="mdi:microsoft-excel" width="18" height="18" style={{ color: '#6b7280' }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Print">
                    <IconButton
                      size="small"
                      onClick={() => handleExport('Print')}
                      sx={{
                        border: '1px solid #e5e7eb',
                        borderRadius: 2,
                        '&:hover': { bgcolor: '#f9fafb', borderColor: '#12C1DC' }
                      }}
                    >
                      <PrintIcon fontSize="small" sx={{ color: '#6b7280' }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Export PDF">
                    <IconButton
                      size="small"
                      onClick={() => handleExport('PDF')}
                      sx={{
                        border: '1px solid #e5e7eb',
                        borderRadius: 2,
                        '&:hover': { bgcolor: '#f9fafb', borderColor: '#12C1DC' }
                      }}
                    >
                      <PdfIcon fontSize="small" sx={{ color: '#6b7280' }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            </Box>

            {/* Enhanced Table */}
            <TableContainer sx={{ maxHeight: '70vh', overflow: 'auto', position: 'relative', width: '100%' }}>
              {(loading || refreshing) && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    bgcolor: 'rgba(255, 255, 255, 0.8)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 10,
                    borderRadius: 1
                  }}
                >
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    transition={{ duration: 0.3 }}
                  >
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                      <CircularProgress size={40} sx={{ color: '#12C1DC' }} />
                      <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 500 }}>
                        {refreshing ? 'Refreshing data...' : 'Loading employees...'}
                      </Typography>
                    </Box>
                  </motion.div>
                </Box>
              )}
              <Table
                stickyHeader
                size={isMobile ? "small" : "medium"}
                aria-label="enhanced employees table"
                sx={{ minWidth: isMobile ? 900 : 1400 }}
              >
                <TableHead>
                  <TableRow sx={{
                    '& .MuiTableCell-root': {
                      backgroundColor: '#f9fafb',
                      borderBottom: '2px solid #e5e7eb'
                    }
                  }}>
                    <TableCell
                      sortDirection={orderBy === 'firstName' ? order : false}
                      sx={{
                        fontWeight: 600,
                        bgcolor: '#f9fafb',
                        color: '#374151',
                        fontSize: '0.875rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em'
                      }}
                    >
                      <TableSortLabel
                        active={orderBy === 'firstName'}
                        direction={orderBy === 'firstName' ? order : 'asc'}
                        onClick={(event) => handleRequestSort(event, 'firstName')}
                        sx={{
                          '& .MuiTableSortLabel-icon': {
                            color: '#12C1DC !important',
                          },
                        }}
                      >
                        Name
                      </TableSortLabel>
                    </TableCell>
                    <TableCell
                      sortDirection={orderBy === 'email' ? order : false}
                      sx={{
                        fontWeight: 600,
                        bgcolor: '#f9fafb',
                        color: '#374151',
                        fontSize: '0.875rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em'
                      }}
                    >
                      <TableSortLabel
                        active={orderBy === 'email'}
                        direction={orderBy === 'email' ? order : 'asc'}
                        onClick={(event) => handleRequestSort(event, 'email')}
                        sx={{
                          '& .MuiTableSortLabel-icon': {
                            color: '#12C1DC !important',
                          },
                        }}
                      >
                        Email
                      </TableSortLabel>
                    </TableCell>
                    {!isMobile && (
                      <TableCell
                        sortDirection={orderBy === 'company' ? order : false}
                        sx={{
                          fontWeight: 600,
                          bgcolor: '#f9fafb',
                          color: '#374151',
                          fontSize: '0.875rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em'
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <TableSortLabel
                            active={orderBy === 'company'}
                            direction={orderBy === 'company' ? order : 'asc'}
                            onClick={(event) => handleRequestSort(event, 'company')}
                            sx={{
                              '& .MuiTableSortLabel-icon': {
                                color: '#12C1DC !important',
                              },
                            }}
                          >
                            Company
                          </TableSortLabel>
                          <IconButton
                            size="small"
                            onClick={(event) => handleFilterClick(event, 'company')}
                            sx={{
                              p: 0.5,
                              color: filters.company !== 'all' ? '#12C1DC' : '#6b7280',
                              '&:hover': { color: '#12C1DC' }
                            }}
                          >
                            <FilterListIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    )}
                    {!isMobile && (
                      <TableCell
                        sortDirection={orderBy === 'position' ? order : false}
                        sx={{
                          fontWeight: 600,
                          bgcolor: '#f9fafb',
                          color: '#374151',
                          fontSize: '0.875rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em'
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <TableSortLabel
                            active={orderBy === 'position'}
                            direction={orderBy === 'position' ? order : 'asc'}
                            onClick={(event) => handleRequestSort(event, 'position')}
                            sx={{
                              '& .MuiTableSortLabel-icon': {
                                color: '#12C1DC !important',
                              },
                            }}
                          >
                            Position
                          </TableSortLabel>
                          <IconButton
                            size="small"
                            onClick={(event) => handleFilterClick(event, 'position')}
                            sx={{
                              p: 0.5,
                              color: filters.position !== 'all' ? '#12C1DC' : '#6b7280',
                              '&:hover': { color: '#12C1DC' }
                            }}
                          >
                            <FilterListIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    )}
                    {!isMobile && (
                      <TableCell
                        sortDirection={orderBy === 'department' ? order : false}
                        sx={{
                          fontWeight: 600,
                          bgcolor: '#f9fafb',
                          color: '#374151',
                          fontSize: '0.875rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em'
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <TableSortLabel
                            active={orderBy === 'department'}
                            direction={orderBy === 'department' ? order : 'asc'}
                            onClick={(event) => handleRequestSort(event, 'department')}
                            sx={{
                              '& .MuiTableSortLabel-icon': {
                                color: '#12C1DC !important',
                              },
                            }}
                          >
                            Department
                          </TableSortLabel>
                          <IconButton
                            size="small"
                            onClick={(event) => handleFilterClick(event, 'department')}
                            sx={{
                              p: 0.5,
                              color: filters.department !== 'all' ? '#12C1DC' : '#6b7280',
                              '&:hover': { color: '#12C1DC' }
                            }}
                          >
                            <FilterListIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    )}
                    <TableCell
                      sortDirection={orderBy === 'isActive' ? order : false}
                      sx={{
                        fontWeight: 600,
                        bgcolor: '#f9fafb',
                        color: '#374151',
                        fontSize: '0.875rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em'
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TableSortLabel
                          active={orderBy === 'isActive'}
                          direction={orderBy === 'isActive' ? order : 'asc'}
                          onClick={(event) => handleRequestSort(event, 'isActive')}
                          sx={{
                            '& .MuiTableSortLabel-icon': {
                              color: '#12C1DC !important',
                            },
                          }}
                        >
                          Status
                        </TableSortLabel>
                        <IconButton
                          size="small"
                          onClick={(event) => handleFilterClick(event, 'status')}
                          sx={{
                            p: 0.5,
                            color: filters.status !== 'all' ? '#12C1DC' : '#6b7280',
                            '&:hover': { color: '#12C1DC' }
                          }}
                        >
                          <FilterListIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                    {!isMobile && (
                      <TableCell sx={{
                        fontWeight: 600,
                        bgcolor: '#f9fafb',
                        color: '#374151',
                        fontSize: '0.875rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em'
                      }}>
                        Store Access
                      </TableCell>
                    )}
                    {!isMobile && (
                      <TableCell sx={{
                        fontWeight: 600,
                        bgcolor: '#f9fafb',
                        color: '#374151',
                        fontSize: '0.875rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em'
                      }}>
                        Group Access
                      </TableCell>
                    )}
                    <TableCell align="right" sx={{
                      fontWeight: 600,
                      bgcolor: '#f9fafb',
                      color: '#374151',
                      fontSize: '0.875rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}>
                      Actions
                    </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <AnimatePresence>
                  {paginatedSortedRows.map((row, index) => {
                    const isEvenRow = index % 2 === 0;
                    return (
                      <motion.tr
                        key={row._id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2, delay: index * 0.05 }}
                        style={{
                          display: 'table-row',
                          backgroundColor: isEvenRow ? '#ffffff' : '#f8f9fa'
                        }}
                        sx={{
                          '&:hover': {
                            backgroundColor: isEvenRow ? '#f0f0f0 !important' : '#e9ecef !important'
                          },
                          '& > .MuiTableCell-root': {
                            borderRight: '1px solid #e0e0e0',
                            borderBottom: '1px solid #e0e0e0'
                          },
                          '& > .MuiTableCell-root:last-child': {
                            borderRight: 'none'
                          }
                        }}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PersonIcon sx={{ color: '#12C1DC', fontSize: '1.2rem' }} />
                            <Box>
                              <Typography variant="body2" fontWeight={600}>
                                {row.firstName} {row.lastName}
                              </Typography>
                              {isMobile && (
                                <Box>
                                  <Typography variant="caption" color="text.secondary">
                                    {row.company || '-'} • {row.position || '-'} • {row.department || '-'}
                                  </Typography>
                                  {(row.storeAccess?.length > 0 || row.groupAccess?.length > 0) && (
                                    <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                      {row.storeAccess?.slice(0, 1).map((store, idx) => (
                                        <Chip
                                          key={`store-${idx}`}
                                          label={store}
                                          size="small"
                                          sx={{
                                            fontSize: '0.7rem',
                                            height: '18px',
                                            bgcolor: '#e8f5e8',
                                            color: '#2e7d32'
                                          }}
                                        />
                                      ))}
                                      {row.groupAccess?.slice(0, 1).map((group, idx) => (
                                        <Chip
                                          key={`group-${idx}`}
                                          label={group}
                                          size="small"
                                          sx={{
                                            fontSize: '0.7rem',
                                            height: '18px',
                                            bgcolor: '#f3e8ff',
                                            color: '#7c3aed'
                                          }}
                                        />
                                      ))}
                                    </Box>
                                  )}
                                </Box>
                              )}
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <EmailIcon sx={{ color: '#6b7280', fontSize: '1rem' }} />
                            <Typography variant="body2">{row.email}</Typography>
                          </Box>
                        </TableCell>
                        {!isMobile && (
                          <TableCell>
                            <Chip
                              label={row.company || '-'}
                              size="small"
                              sx={{
                                bgcolor: '#e8f5e8',
                                color: '#2e7d32',
                                fontWeight: 500
                              }}
                            />
                          </TableCell>
                        )}
                        {!isMobile && (
                          <TableCell>
                            <Chip
                              label={row.position || '-'}
                              size="small"
                              sx={{
                                bgcolor: '#e3f2fd',
                                color: '#1976d2',
                                fontWeight: 500
                              }}
                            />
                          </TableCell>
                        )}
                        {!isMobile && (
                          <TableCell>
                            <Chip
                              label={row.department || '-'}
                              size="small"
                              sx={{
                                bgcolor: '#f3e5f5',
                                color: '#7b1fa2',
                                fontWeight: 500
                              }}
                            />
                          </TableCell>
                        )}
                        <TableCell>
                          <Chip
                            icon={row.isActive ? <ToggleOnIcon /> : <ToggleOffIcon />}
                            label={row.isActive ? 'Active' : 'Inactive'}
                            size="small"
                            sx={{
                              bgcolor: row.isActive ? '#e8f5e8' : '#ffebee',
                              color: row.isActive ? '#2e7d32' : '#c62828',
                              fontWeight: 500,
                              '& .MuiChip-icon': {
                                color: row.isActive ? '#2e7d32' : '#c62828'
                              }
                            }}
                          />
                        </TableCell>
                        {!isMobile && (
                          <TableCell>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {row.storeAccess && row.storeAccess.length > 0 ? (
                                row.storeAccess.slice(0, 2).map((store, idx) => (
                                  <Chip
                                    key={idx}
                                    label={store}
                                    size="small"
                                    sx={{
                                      fontSize: '0.75rem',
                                      height: '20px',
                                      bgcolor: '#e8f5e8',
                                      color: '#2e7d32'
                                    }}
                                  />
                                ))
                              ) : (
                                <Typography variant="caption" color="text.secondary">-</Typography>
                              )}
                              {row.storeAccess && row.storeAccess.length > 2 && (
                                <Chip
                                  label={`+${row.storeAccess.length - 2}`}
                                  size="small"
                                  sx={{
                                    fontSize: '0.75rem',
                                    height: '20px',
                                    bgcolor: '#f3f4f6',
                                    color: '#6b7280'
                                  }}
                                />
                              )}
                            </Box>
                          </TableCell>
                        )}
                        {!isMobile && (
                          <TableCell>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {row.groupAccess && row.groupAccess.length > 0 ? (
                                row.groupAccess.slice(0, 2).map((group, idx) => (
                                  <Chip
                                    key={idx}
                                    label={group}
                                    size="small"
                                    sx={{
                                      fontSize: '0.75rem',
                                      height: '20px',
                                      bgcolor: '#f3e8ff',
                                      color: '#7c3aed'
                                    }}
                                  />
                                ))
                              ) : (
                                <Typography variant="caption" color="text.secondary">-</Typography>
                              )}
                              {row.groupAccess && row.groupAccess.length > 2 && (
                                <Chip
                                  label={`+${row.groupAccess.length - 2}`}
                                  size="small"
                                  sx={{
                                    fontSize: '0.75rem',
                                    height: '20px',
                                    bgcolor: '#f3f4f6',
                                    color: '#6b7280'
                                  }}
                                />
                              )}
                            </Box>
                          </TableCell>
                        )}
                        <TableCell align="right">
                          <Stack direction="row" spacing={1} justifyContent="flex-end">
                            {mainTabValue === 0 ? (
                              // All Employees Tab - show normal actions
                              <>
                                {row.approvalStatus === 'approved' && (
                                  <Tooltip title="Edit employee">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleOpen(row)}
                                      sx={{
                                        color: '#12C1DC',
                                        '&:hover': { bgcolor: 'rgba(18, 193, 220, 0.1)' }
                                      }}
                                    >
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                {(row.approvalStatus === 'approved' || row.approvalStatus === 'pending') && isSuperAdmin && (
                                  <Tooltip title="Transfer employee">
                                    <span>
                                      <IconButton
                                        size="small"
                                        onClick={() => openTransfer(row)}
                                        disabled={row.role === 'store'}
                                        sx={{
                                          color: '#10b981',
                                          '&:hover': { bgcolor: 'rgba(16, 185, 129, 0.1)' }
                                        }}
                                      >
                                        <Icon icon="mdi:arrow-right-bold" width={18} height={18} />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                )}
                                {(row.approvalStatus === 'approved' || row.approvalStatus === 'rejected') && (
                                  <Tooltip title="Delete employee">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleDelete(row)}
                                      sx={{
                                        color: '#ef4444',
                                        '&:hover': { bgcolor: 'rgba(239, 68, 68, 0.1)' }
                                      }}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                {row.approvalStatus === 'pending' && isSuperAdmin && (
                                  <>
                                    <Tooltip title="Approve">
                                      <IconButton
                                        size="small"
                                        onClick={() => openApprove(row)}
                                        sx={{ color: '#22c55e', '&:hover': { bgcolor: 'rgba(34,197,94,0.1)' } }}
                                      >
                                        <Icon icon="mdi:check-bold" width={18} height={18} />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Reject">
                                      <IconButton
                                        size="small"
                                        onClick={() => openReject(row)}
                                        sx={{ color: '#ef4444', '&:hover': { bgcolor: 'rgba(239,68,68,0.1)' } }}
                                      >
                                        <Icon icon="mdi:close-thick" width={18} height={18} />
                                      </IconButton>
                                    </Tooltip>
                                  </>
                                )}
                              </>
                            ) : (
                              // Rejected Employees Tab - show simplified actions
                              <>
                                <Tooltip title="Re-approve employee">
                                  <IconButton
                                    size="small"
                                    onClick={() => openApprove(row)}
                                    sx={{ color: '#22c55e', '&:hover': { bgcolor: 'rgba(34,197,94,0.1)' } }}
                                  >
                                    <Icon icon="mdi:account-check" width={18} height={18} />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Delete employee">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleDelete(row)}
                                    sx={{
                                      color: '#ef4444',
                                      '&:hover': { bgcolor: 'rgba(239, 68, 68, 0.1)' }
                                    }}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </>
                            )}
                          </Stack>
                        </TableCell>
                      </motion.tr>
                    );
                  })}
                </AnimatePresence>
              </TableBody>
            </Table>
          </TableContainer>

          {/* Filter Popover */}
          <Popover
            open={Boolean(filterAnchorEl)}
            anchorEl={filterAnchorEl}
            onClose={handleFilterClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
          >
            <Box sx={{ p: 2, minWidth: 200 }}>
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                Filter by {filterColumn}
              </Typography>

              {filterColumn === 'status' && (
                <FormControl component="fieldset" sx={{ width: '100%' }}>
                  <MenuItem
                    value="all"
                    selected={filters.status === 'all'}
                    onClick={() => handleFilterChange('status', 'all')}
                    sx={{ py: 1 }}
                  >
                    All Status
                  </MenuItem>
                  <MenuItem
                    value="active"
                    selected={filters.status === 'active'}
                    onClick={() => handleFilterChange('status', 'active')}
                    sx={{ py: 1 }}
                  >
                    Active
                  </MenuItem>
                  <MenuItem
                    value="inactive"
                    selected={filters.status === 'inactive'}
                    onClick={() => handleFilterChange('status', 'inactive')}
                    sx={{ py: 1 }}
                  >
                    Inactive
                  </MenuItem>
                </FormControl>
              )}

              {filterColumn === 'company' && (
                <FormControl component="fieldset" sx={{ width: '100%' }}>
                  <MenuItem
                    value="all"
                    selected={filters.company === 'all'}
                    onClick={() => handleFilterChange('company', 'all')}
                    sx={{ py: 1 }}
                  >
                    All Companies
                  </MenuItem>
                  {uniqueCompanies.map((company) => (
                    <MenuItem
                      key={company}
                      value={company}
                      selected={filters.company === company}
                      onClick={() => handleFilterChange('company', company)}
                      sx={{ py: 1 }}
                    >
                      {company}
                    </MenuItem>
                  ))}
                </FormControl>
              )}

              {filterColumn === 'position' && (
                <FormControl component="fieldset" sx={{ width: '100%' }}>
                  <MenuItem
                    value="all"
                    selected={filters.position === 'all'}
                    onClick={() => handleFilterChange('position', 'all')}
                    sx={{ py: 1 }}
                  >
                    All Positions
                  </MenuItem>
                  {uniquePositions.map((position) => (
                    <MenuItem
                      key={position}
                      value={position}
                      selected={filters.position === position}
                      onClick={() => handleFilterChange('position', position)}
                      sx={{ py: 1 }}
                    >
                      {position}
                    </MenuItem>
                  ))}
                </FormControl>
              )}

              {filterColumn === 'department' && (
                <FormControl component="fieldset" sx={{ width: '100%' }}>
                  <MenuItem
                    value="all"
                    selected={filters.department === 'all'}
                    onClick={() => handleFilterChange('department', 'all')}
                    sx={{ py: 1 }}
                  >
                    All Departments
                  </MenuItem>
                  {uniqueDepartments.map((department) => (
                    <MenuItem
                      key={department}
                      value={department}
                      selected={filters.department === department}
                      onClick={() => handleFilterChange('department', department)}
                      sx={{ py: 1 }}
                    >
                      {department}
                    </MenuItem>
                  ))}
                </FormControl>
              )}

              <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                <Button
                  size="small"
                  onClick={handleFilterClose}
                  sx={{ textTransform: 'none' }}
                >
                  Close
                </Button>
                {(filters.status !== 'all' || filters.company !== 'all' || filters.position !== 'all' || filters.department !== 'all') && (
                  <Button
                    size="small"
                    onClick={clearAllFilters}
                    sx={{ textTransform: 'none', color: '#12C1DC' }}
                  >
                    Clear All
                  </Button>
                )}
              </Box>
            </Box>
          </Popover>

          {/* Transfer Dialog */}
          <Dialog
            open={transferOpen}
            onClose={() => setTransferOpen(false)}
            fullWidth
            maxWidth="sm"
          >
            <DialogTitle>Transfer Employee</DialogTitle>
            <DialogContent dividers>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Select a new supervisor to transfer this employee under.
              </Typography>
              <Autocomplete
                options={potentialMothers}
                getOptionLabel={(opt) => `${opt.firstName} ${opt.lastName} (${opt.role})`}
                value={potentialMothers.find(p => p._id === transferMotherId) || null}
                onChange={(e, value) => setTransferMotherId(value?._id || '')}
                renderInput={(params) => (
                  <MuiTextField {...params} label="New Supervisor" placeholder="Choose user" />
                )}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setTransferOpen(false)}>Cancel</Button>
              <Button onClick={doTransfer} disabled={!transferMotherId || loading} variant="contained">
                {loading ? 'Transferring...' : 'Transfer'}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Approve Dialog */}
          <Dialog open={approveOpen} onClose={() => setApproveOpen(false)} fullWidth maxWidth="sm">
            <DialogTitle>Approve Account</DialogTitle>
            <DialogContent dividers>
              <Typography variant="body2" sx={{ mb: 2 }}>Assign group/store access and add optional remarks.</Typography>
              <Autocomplete
                multiple
                options={availableGroups}
                value={approveGroups}
                onChange={(e, v) => setApproveGroups(v)}
                renderInput={(params) => <MuiTextField {...params} label="Groups" placeholder="Select groups" />}
                sx={{ mb: 2 }}
              />
              <Autocomplete
                multiple
                options={availableStores}
                value={approveStores}
                onChange={(e, v) => setApproveStores(v)}
                renderInput={(params) => <MuiTextField {...params} label="Stores" placeholder="Select stores" />}
                sx={{ mb: 2 }}
              />
              <TextField
                label="Remarks"
                fullWidth
                multiline
                minRows={2}
                value={approveRemarks}
                onChange={(e) => setApproveRemarks(e.target.value)}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setApproveOpen(false)}>Cancel</Button>
              <Button variant="contained" onClick={confirmApprove} disabled={loading}>
                {loading ? 'Approving...' : 'Approve'}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Reject Dialog */}
          <Dialog open={rejectOpen} onClose={() => setRejectOpen(false)} fullWidth maxWidth="sm">
            <DialogTitle>Reject Account</DialogTitle>
            <DialogContent dividers>
              <Typography variant="body2" sx={{ mb: 2 }}>Provide remarks for rejection.</Typography>
              <TextField
                label="Remarks"
                fullWidth
                multiline
                minRows={2}
                value={rejectRemarks}
                onChange={(e) => setRejectRemarks(e.target.value)}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setRejectOpen(false)}>Cancel</Button>
              <Button variant="contained" color="error" onClick={confirmReject} disabled={loading}>
                {loading ? 'Rejecting...' : 'Reject'}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Pagination */}
          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 2,
            gap: { xs: 2, sm: 0 }
          }}>
              <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 500 }}>
              Showing {paginatedSortedRows.length > 0 ? (currentPage - 1) * rowsPerPage + 1 : 0} to {Math.min(currentPage * rowsPerPage, filteredRows.length)} of {filteredRows.length} entries
            </Typography>
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={(event, page) => setCurrentPage(page)}
              size={isMobile ? "small" : "medium"}
              sx={{
                '& .MuiPaginationItem-root': {
                  borderRadius: '8px',
                  '&.Mui-selected': {
                    backgroundColor: '#12C1DC',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: '#0FABDB'
                    }
                  }
                }
              }}
            />
          </Box>

        </Paper>
      </motion.div>

        {/* Enhanced Dialog Form */}
        <Dialog
          open={open}
          onClose={() => setOpen(false)}
          fullWidth
          maxWidth="md"
          PaperProps={{
            sx: { borderRadius: 2 }
          }}
        >
          <DialogTitle sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            bgcolor: '#f8f9fa',
            borderBottom: '1px solid #e5e7eb'
          }}>
            <PersonIcon sx={{ color: '#12C1DC' }} />
            {editingId ? 'Edit Employee' : 'Add New Employee'}
          </DialogTitle>
          <DialogContent sx={{ p: 0 }}>
            {error && (
              <Alert severity="error" sx={{ m: 3, mb: 0 }}>{error}</Alert>
            )}

            {editingId ? (
              <Tabs
                value={tabValue}
                onChange={(event, newValue) => setTabValue(newValue)}
                sx={{
                  borderBottom: 1,
                  borderColor: 'divider',
                  '& .MuiTab-root': {
                    minHeight: 48,
                    textTransform: 'none',
                    fontWeight: 500
                  }
                }}
              >
                <Tab
                  icon={<PersonIcon />}
                  label="Employee Info"
                  iconPosition="start"
                  sx={{ px: 3 }}
                />
                <Tab
                  icon={<VpnKeyIcon />}
                  label="Change Password"
                  iconPosition="start"
                  sx={{ px: 3 }}
                />
              </Tabs>
            ) : null}

            <Box sx={{ p: 3 }}>
              {(!editingId || tabValue === 0) && (
                <>
                  {/* Personal Information Section */}
                  <Typography variant="h6" sx={{ mb: 2, color: '#374151', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonIcon sx={{ fontSize: '1.2rem', color: '#12C1DC' }} />
                    Personal Information
                  </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 3 }}>
                <TextField 
                  label="First Name" 
                  value={form.firstName} 
                  onChange={(e) => {
                    setForm({ ...form, firstName: e.target.value });
                    if (touched.firstName) validateField('firstName', e.target.value);
                  }}
                  onBlur={(e) => {
                    setTouched(prev => ({ ...prev, firstName: true }));
                    validateField('firstName', e.target.value);
                  }}
                  error={touched.firstName && !!validationErrors.firstName}
                  helperText={touched.firstName && validationErrors.firstName}
                  fullWidth 
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon sx={{ color: '#9ca3af', fontSize: '1.1rem' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': { borderColor: '#12C1DC' },
                      '&.Mui-focused fieldset': { borderColor: '#12C1DC' }
                    }
                  }}
                />
                <TextField 
                  label="Last Name" 
                  value={form.lastName} 
                  onChange={(e) => {
                    setForm({ ...form, lastName: e.target.value });
                    if (touched.lastName) validateField('lastName', e.target.value);
                  }}
                  onBlur={(e) => {
                    setTouched(prev => ({ ...prev, lastName: true }));
                    validateField('lastName', e.target.value);
                  }}
                  error={touched.lastName && !!validationErrors.lastName}
                  helperText={touched.lastName && validationErrors.lastName}
                  fullWidth 
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon sx={{ color: '#9ca3af', fontSize: '1.1rem' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': { borderColor: '#12C1DC' },
                      '&.Mui-focused fieldset': { borderColor: '#12C1DC' }
                    }
                  }}
                />
              </Box>
              
              <TextField 
                type="email" 
                label="Email Address" 
                value={form.email} 
                onChange={(e) => {
                  setForm({ ...form, email: e.target.value });
                  if (touched.email) validateField('email', e.target.value);
                }}
                onBlur={(e) => {
                  setTouched(prev => ({ ...prev, email: true }));
                  validateField('email', e.target.value);
                }}
                error={touched.email && !!validationErrors.email}
                helperText={touched.email && validationErrors.email}
                fullWidth 
                sx={{ mb: 3 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon sx={{ color: '#9ca3af', fontSize: '1.1rem' }} />
                    </InputAdornment>
                  ),
                }}
                FormHelperTextProps={{
                  sx: { 
                    mx: 0, 
                    mt: 0.5,
                    fontSize: '0.75rem'
                  }
                }}
              />

              <TextField
                label="Company"
                value={form.company}
                onChange={(e) => {
                  setForm({ ...form, company: e.target.value });
                  if (touched.company) validateField('company', e.target.value);
                }}
                onBlur={(e) => {
                  setTouched(prev => ({ ...prev, company: true }));
                  validateField('company', e.target.value);
                }}
                error={touched.company && !!validationErrors.company}
                helperText={touched.company && validationErrors.company}
                fullWidth
                sx={{ mb: 3 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <BusinessIcon sx={{ color: '#9ca3af', fontSize: '1.1rem' }} />
                    </InputAdornment>
                  ),
                }}
                FormHelperTextProps={{
                  sx: {
                    mx: 0,
                    mt: 0.5,
                    fontSize: '0.75rem'
                  }
                }}
              />

              {/* Role Selection (restricted by hierarchy) */}
              <Box sx={{ mb: 3 }}>
                <Autocomplete
                  id="role-select"
                  options={roleOptions}
                  value={form.role}
                  onChange={(event, newValue) => setForm(prev => ({ ...prev, role: newValue || '' }))}
                  disableClearable
                  renderInput={(params) => (
                    <MuiTextField
                      {...params}
                      label="Role"
                      placeholder="Select role"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '&:hover fieldset': { borderColor: '#12C1DC' },
                          '&.Mui-focused fieldset': { borderColor: '#12C1DC' }
                        }
                      }}
                    />
                  )}
                />
                {form.role === '' && (
                  <Typography variant="caption" color="error">Role is required</Typography>
                )}
              </Box>

              {/* Store Access Section */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 2, color: '#374151', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <StoreIcon sx={{ fontSize: '1.2rem', color: '#12C1DC' }} />
                  Store Access Control
                </Typography>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Specify which store payrolls this employee can access. Leave empty for full access.
                </Typography>

                {/* Store Autocomplete Field */}
                <Autocomplete
                  multiple
                  id="store-access-autocomplete"
                  options={assignableStores}
                  value={form.storeAccess}
                  onChange={(event, newValue) => {
                    setForm(prev => ({
                      ...prev,
                      storeAccess: newValue,
                    }));
                    // Clear validation errors when user makes a selection
                    if (validationErrors.storeAccess) {
                      setValidationErrors(prev => ({
                        ...prev,
                        storeAccess: null,
                        groupAccess: null
                      }));
                    }
                  }}
                  disabled={assignableStores.length === 0}
                  loading={storesLoading}
                  renderInput={(params) => (
                    <MuiTextField
                      {...params}
                      label="Select Stores"
                      placeholder="Choose stores..."
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '&:hover fieldset': { borderColor: '#12C1DC' },
                          '&.Mui-focused fieldset': { borderColor: '#12C1DC' }
                        }
                      }}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <InputAdornment position="start">
                              <StoreIcon sx={{ color: '#9ca3af', fontSize: '1.1rem' }} />
                            </InputAdornment>
                            {params.InputProps.startAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        label={option}
                        size="small"
                        sx={{
                          bgcolor: '#e3f2fd',
                          color: '#1976d2',
                          fontWeight: 500,
                          '& .MuiChip-deleteIcon': {
                            color: '#1976d2',
                            '&:hover': {
                              color: '#1565c0'
                            }
                          }
                        }}
                        {...getTagProps({ index })}
                      />
                    ))
                  }
                  sx={{ mb: 2 }}
                />

                {form.storeAccess.length === 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    No store access assigned
                  </Typography>
                )}
                {touched.storeAccess && validationErrors.storeAccess && (
                  <Typography variant="caption" color="error">{validationErrors.storeAccess}</Typography>
                )}
              </Box>

              {/* Group Access Section */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 2, color: '#374151', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <GroupIcon sx={{ fontSize: '1.2rem', color: '#12C1DC' }} />
                  Group Access Control
                </Typography>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Specify which groups this employee can access. Leave empty for full access.
                </Typography>

                {/* Group Autocomplete Field */}
                <Autocomplete
                  multiple
                  id="group-access-autocomplete"
                  options={assignableGroups}
                  value={form.groupAccess}
                  onChange={(event, newValue) => {
                    setForm(prev => ({
                      ...prev,
                      groupAccess: newValue
                    }));
                    // Clear validation errors when user makes a selection
                    if (validationErrors.groupAccess) {
                      setValidationErrors(prev => ({
                        ...prev,
                        groupAccess: null,
                        storeAccess: null
                      }));
                    }
                  }}
                  disabled={assignableGroups.length === 0}
                  loading={groupsLoading}
                  renderInput={(params) => (
                    <MuiTextField
                      {...params}
                      label="Select Groups"
                      placeholder="Choose groups..."
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '&:hover fieldset': { borderColor: '#12C1DC' },
                          '&.Mui-focused fieldset': { borderColor: '#12C1DC' }
                        }
                      }}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <InputAdornment position="start">
                              <GroupIcon sx={{ color: '#9ca3af', fontSize: '1.1rem' }} />
                            </InputAdornment>
                            {params.InputProps.startAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        label={option}
                        size="small"
                        sx={{
                          bgcolor: '#f3e8ff',
                          color: '#7c3aed',
                          fontWeight: 500,
                          '& .MuiChip-deleteIcon': {
                            color: '#7c3aed',
                            '&:hover': {
                              color: '#6d28d9'
                            }
                          }
                        }}
                        {...getTagProps({ index })}
                      />
                    ))
                  }
                  sx={{ mb: 2 }}
                />

                {form.groupAccess.length === 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    No group access assigned
                  </Typography>
                )}
                {touched.groupAccess && validationErrors.groupAccess && (
                  <Typography variant="caption" color="error">{validationErrors.groupAccess}</Typography>
                )}
                {!isSuperAdmin && assignableGroups.length === 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mt: 1 }}>
                    You don't have group access to assign to employees
                  </Typography>
                )}
              </Box>

              {canManageDeductionAccess && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" sx={{ mb: 2, color: '#374151', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Icon icon="mdi:view-dashboard-outline" width="1.2rem" style={{ color: '#12C1DC' }} />
                    Payroll Access Control
                  </Typography>

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Control sidebar access for Masterfile, Payroll, Employees, Reports, and Settings. Default is enabled.
                  </Typography>

                  <Autocomplete
                    multiple
                    id="payroll-access-autocomplete"
                    options={PAYROLL_ACCESS_TABS}
                    value={PAYROLL_ACCESS_TABS.filter((key) => Boolean(normalizePayrollAccessControl(form.payrollAccessControl)[key]))}
                    onChange={(event, newValue) => {
                      const selected = new Set(newValue || []);
                      setForm((prev) => ({
                        ...prev,
                        payrollAccessControl: PAYROLL_ACCESS_TABS.reduce((acc, key) => {
                          acc[key] = selected.has(key);
                          return acc;
                        }, {})
                      }));
                    }}
                    renderInput={(params) => (
                      <MuiTextField
                        {...params}
                        label="Select Payroll Tabs"
                        placeholder="Choose tabs..."
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '&:hover fieldset': { borderColor: '#12C1DC' },
                            '&.Mui-focused fieldset': { borderColor: '#12C1DC' }
                          }
                        }}
                      />
                    )}
                    getOptionLabel={(option) => option.charAt(0).toUpperCase() + option.slice(1)}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          label={option.charAt(0).toUpperCase() + option.slice(1)}
                          size="small"
                          sx={{
                            bgcolor: '#ecfeff',
                            color: '#0f766e',
                            fontWeight: 500,
                            '& .MuiChip-deleteIcon': {
                              color: '#0f766e',
                              '&:hover': {
                                color: '#115e59'
                              }
                            }
                          }}
                          {...getTagProps({ index })}
                        />
                      ))
                    }
                    sx={{ mb: 2 }}
                  />

                  {Object.values(normalizePayrollAccessControl(form.payrollAccessControl)).every((value) => value === true) && (
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mb: 2 }}>
                      Full payroll sidebar access enabled
                    </Typography>
                  )}

                  <Typography variant="h6" sx={{ mb: 2, color: '#374151', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Icon icon="mdi:cash-minus" width="1.2rem" style={{ color: '#12C1DC' }} />
                    Deduction Access Control
                  </Typography>

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Specify which deductions this employee can access.
                  </Typography>

                  <Autocomplete
                    multiple
                    id="deduction-access-autocomplete"
                    options={DEDUCTION_ACCESS_OPTIONS}
                    value={form.deductionAccess}
                    onChange={(event, newValue) => {
                      setForm(prev => ({
                        ...prev,
                        deductionAccess: newValue
                      }));
                    }}
                    renderInput={(params) => (
                      <MuiTextField
                        {...params}
                        label="Select Deductions"
                        placeholder="Choose deductions..."
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '&:hover fieldset': { borderColor: '#12C1DC' },
                            '&.Mui-focused fieldset': { borderColor: '#12C1DC' }
                          }
                        }}
                      />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          label={option}
                          size="small"
                          sx={{
                            bgcolor: '#ecfeff',
                            color: '#0f766e',
                            fontWeight: 500,
                            '& .MuiChip-deleteIcon': {
                              color: '#0f766e',
                              '&:hover': {
                                color: '#115e59'
                              }
                            }
                          }}
                          {...getTagProps({ index })}
                        />
                      ))
                    }
                    sx={{ mb: 2 }}
                  />

                  {form.deductionAccess.length === 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      No deduction access assigned
                    </Typography>
                  )}
                </Box>
              )}

              {/* Account Status Toggle */}
              <Box sx={{ mb: 3 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.isActive}
                      onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                      color="primary"
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2">
                        Account {form.isActive ? 'Active' : 'Inactive'}
                      </Typography>
                    </Box>
                  }
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4 }}>
                  {form.isActive ? 'Employee can access the system' : 'Employee cannot access the system'}
                </Typography>
              </Box>

              {/* Approval Toggle removed per request */}

              {!editingId && (
                <TextField 
                  type="password" 
                  label="Password" 
                  value={form.password} 
                  onChange={(e) => {
                    setForm({ ...form, password: e.target.value });
                    if (touched.password) validateField('password', e.target.value);
                  }}
                  onBlur={(e) => {
                    setTouched(prev => ({ ...prev, password: true }));
                    validateField('password', e.target.value);
                  }}
                  error={touched.password && !!validationErrors.password}
                  helperText={touched.password && validationErrors.password || 'Minimum 6 characters required'}
                  fullWidth 
                  sx={{ mb: 3 }}
                  FormHelperTextProps={{
                    sx: { 
                      mx: 0, 
                      mt: 0.5,
                      fontSize: '0.75rem'
                    }
                  }}
                />
              )}
              
              {/* Work Information Section */}
              <Typography variant="h6" sx={{ mb: 2, color: '#374151', display: 'flex', alignItems: 'center', gap: 1 }}>
                <WorkIcon sx={{ fontSize: '1.2rem', color: '#12C1DC' }} />
                Work Information
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                <TextField 
                  label="Position" 
                  value={form.position} 
                  onChange={(e) => {
                    setForm({ ...form, position: e.target.value });
                    if (touched.position) validateField('position', e.target.value);
                  }}
                  onBlur={(e) => {
                    setTouched(prev => ({ ...prev, position: true }));
                    validateField('position', e.target.value);
                  }}
                  error={touched.position && !!validationErrors.position}
                  helperText={touched.position && validationErrors.position}
                  fullWidth 
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <WorkIcon sx={{ color: '#9ca3af', fontSize: '1.1rem' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': { borderColor: '#12C1DC' },
                      '&.Mui-focused fieldset': { borderColor: '#12C1DC' }
                    }
                  }}
                />
                <TextField 
                  label="Department" 
                  value={form.department} 
                  onChange={(e) => {
                    setForm({ ...form, department: e.target.value });
                    if (touched.department) validateField('department', e.target.value);
                  }}
                  onBlur={(e) => {
                    setTouched(prev => ({ ...prev, department: true }));
                    validateField('department', e.target.value);
                  }}
                  error={touched.department && !!validationErrors.department}
                  helperText={touched.department && validationErrors.department}
                  fullWidth 
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <BusinessIcon sx={{ color: '#9ca3af', fontSize: '1.1rem' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': { borderColor: '#12C1DC' },
                      '&.Mui-focused fieldset': { borderColor: '#12C1DC' }
                    }
                  }}
                />
              </Box>
                </>
              )}

              {editingId && tabValue === 1 && (
                <>
                  {/* Password Change Section */}
                  <Typography variant="h6" sx={{ mb: 2, color: '#374151', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <VpnKeyIcon sx={{ fontSize: '1.2rem', color: '#12C1DC' }} />
                    Change Password
                  </Typography>

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Update the employee's password. The employee will need to use the new password on their next login.
                  </Typography>

                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 3 }}>
                    <TextField
                      type="password"
                      label="New Password"
                      value={passwordForm.newPassword}
                      onChange={(e) => {
                        setPasswordForm({ ...passwordForm, newPassword: e.target.value });
                        if (passwordTouched.newPassword) validatePasswordField('newPassword', e.target.value);
                        if (passwordTouched.confirmPassword) validatePasswordField('confirmPassword', passwordForm.confirmPassword);
                      }}
                      onBlur={(e) => {
                        setPasswordTouched(prev => ({ ...prev, newPassword: true }));
                        validatePasswordField('newPassword', passwordForm.newPassword);
                      }}
                      error={passwordTouched.newPassword && !!passwordValidationErrors.newPassword}
                      helperText={passwordTouched.newPassword && passwordValidationErrors.newPassword || 'Minimum 6 characters required'}
                      fullWidth
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <VpnKeyIcon sx={{ color: '#9ca3af', fontSize: '1.1rem' }} />
                          </InputAdornment>
                        ),
                      }}
                      FormHelperTextProps={{
                        sx: {
                          mx: 0,
                          mt: 0.5,
                          fontSize: '0.75rem'
                        }
                      }}
                    />
                    <TextField
                      type="password"
                      label="Confirm New Password"
                      value={passwordForm.confirmPassword}
                      onChange={(e) => {
                        setPasswordForm({ ...passwordForm, confirmPassword: e.target.value });
                        if (passwordTouched.confirmPassword) validatePasswordField('confirmPassword', e.target.value);
                      }}
                      onBlur={(e) => {
                        setPasswordTouched(prev => ({ ...prev, confirmPassword: true }));
                        validatePasswordField('confirmPassword', passwordForm.confirmPassword);
                      }}
                      error={passwordTouched.confirmPassword && !!passwordValidationErrors.confirmPassword}
                      helperText={passwordTouched.confirmPassword && passwordValidationErrors.confirmPassword}
                      fullWidth
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <VpnKeyIcon sx={{ color: '#9ca3af', fontSize: '1.1rem' }} />
                          </InputAdornment>
                        ),
                      }}
                      FormHelperTextProps={{
                        sx: {
                          mx: 0,
                          mt: 0.5,
                          fontSize: '0.75rem'
                        }
                      }}
                    />
                  </Box>
                </>
              )}
            </Box>
        </DialogContent>
          <DialogActions sx={{ p: 3, bgcolor: '#f8f9fa', gap: 2 }}>
            <Button 
              onClick={() => setOpen(false)}
              sx={{ 
                textTransform: 'none',
                color: '#6b7280',
                '&:hover': { bgcolor: 'rgba(107, 114, 128, 0.1)' }
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={loading || (tabValue === 0 ? Object.values(validationErrors).some(error => error !== null) : Object.values(passwordValidationErrors).some(error => error !== null))}
              variant="contained"
              startIcon={loading ? <CircularProgress size={16} sx={{ color: 'white' }} /> : (tabValue === 0 ? <PersonIcon /> : <VpnKeyIcon />)}
              sx={{
                bgcolor: '#12C1DC',
                '&:hover': { bgcolor: '#0FABDB' },
                textTransform: 'none',
                minWidth: '120px'
              }}
            >
              {loading ? 'Saving...' : (editingId ? (tabValue === 0 ? 'Update Employee' : 'Update Password') : 'Create')}
            </Button>
        </DialogActions>
      </Dialog>
    </Box>
    </Fade>
  );
};

export default Employee;


